import type { ComponentType, FC } from 'react';
import { useState } from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import {
  BiData,
  BuyCoreButtonBiEvent,
  StatsChangeLevelClickBiEvent,
} from '@wix/da-bi/pkg/events';
import Surface from '@wix/da-ds/pkg/Surface';
import { ThemeSurface } from '@wix/da-react-context/pkg/constants';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import { Section } from '@wix/da-a11y/pkg/components/headings/tagWrappers';
import SelectField from '@wix/da-ds/pkg/Dropdown/select/SelectField';
import { type StringMap, TOptions } from 'i18next';
import ReportsTableRowMobile from '../ReportsTableRowMobile';
import ReportsDataRowMobile from '../ReportsDataRowMobile';
import ReportsDataTotalRowMobile from '../ReportsDataTotalRowMobile';
import SectionEmptyState from '../../../../SectionEmptyState';
import { getSlice } from '../../../../../../selectors/reports';
import { TimePeriod, TimePeriodOption } from '../../../../../../types/reports';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import ArrowDown from '@wix/da-ds/pkg/Icons/ArrowDown';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import { Url } from '@wix/da-url';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import { getCoreLevel } from '@wix/da-shared-react/pkg/publicSession/selectors';
import { generateReportsTableItemKey } from '../../../../utils/reportItemKeys';

import s from './GenericReportMobile.scss';

interface ColumnDef {
  key: string;
  label: string;
  format?: (value: any, row: any) => any;
  className?: string;
}

interface GenericReportMobileProps {
  reportType:
    | 'soldProducts'
    | 'soldSubscriptions'
    | 'topSellingProducts'
    | 'topCustomers'
    | 'soldExclusives'
    | 'topSubscribers'
    | 'subsCancellationReasons';
  headingKey: string;
  descriptionKey: string;
  descriptionTranslationOptions?: TOptions<StringMap> | string;
  columns: ColumnDef[];
  RowHeading: ComponentType<{ item: any; index: number }>;
  RowExtras?: ComponentType<{ item: any }>;
  showTotal?: boolean;
  initialVisibleCount?: number;
  proOnlydisplayLimit?: number;
  /**
   * Custom time period options for this report. If not provided, default time periods will be used.
   */
  timePeriodOptions?: TimePeriodOption[];
  compact?: boolean;
}

const GenericReportMobile: FC<GenericReportMobileProps> = ({
  reportType,
  headingKey,
  descriptionKey,
  descriptionTranslationOptions,
  columns,
  RowHeading,
  RowExtras,
  showTotal = false,
  proOnlydisplayLimit,
  initialVisibleCount,
  timePeriodOptions,
  compact = false,
}) => {
  const { t } = useTranslation();
  const slice = useSelector(getSlice);
  const userCoreLevel = useSelector(getCoreLevel);
  const hasCoreProPlus =
    userCoreLevel === 'core_pro_plus' || userCoreLevel === 'core_max';

  const displaySlice = slice || {};

  const defaultTimePeriodOptions: TimePeriodOption[] = [
    {
      value: TimePeriod.PAST_MONTH,
      translationKey: `studio.reports.timePeriods.${TimePeriod.PAST_MONTH}`,
    },
    {
      value: TimePeriod.PAST_YEAR,
      translationKey: `studio.reports.timePeriods.${TimePeriod.PAST_YEAR}`,
    },
    {
      value: TimePeriod.ALL,
      translationKey: `studio.reports.timePeriods.${TimePeriod.ALL}`,
    },
  ];

  const availableTimePeriods = timePeriodOptions || defaultTimePeriodOptions;

  const [timePeriod, setTimePeriod] = useState<TimePeriod>(() => {
    const avail = displaySlice?.[reportType]?.timePeriods;
    return (
      availableTimePeriods
        .map(option => option.value)
        .find(p => (avail?.[p]?.length ?? 0) > 0) ??
      availableTimePeriods[0].value
    );
  });
  const [isShowingAllRows, setIsShowingAllRows] = useState(false);

  if (!displaySlice) return null;

  const rawData: any[] =
    displaySlice[reportType]?.timePeriods?.[timePeriod] || [];
  const tableData = rawData.filter(
    item =>
      item.productType !== 'Total' &&
      item.productName !== 'Total' &&
      item.exclusiveType !== 'total' // •`_´•
  );
  // Sort subsCancellationReasons data from biggest percentage to smallest (same as desktop)
  const sortedTableData =
    reportType === 'subsCancellationReasons'
      ? [...tableData].sort((a, b) => {
          const aPercentage = a.percentage ?? 0;
          const bPercentage = b.percentage ?? 0;
          return bPercentage - aPercentage;
        })
      : tableData;
  const tableDataSlice = hasCoreProPlus
    ? isShowingAllRows
      ? sortedTableData
      : sortedTableData.slice(0, initialVisibleCount)
    : sortedTableData.slice(0, proOnlydisplayLimit);

  const totalData = rawData.find(
    item =>
      item.productType === 'Total' ||
      item.productName === 'Total' ||
      item.exclusiveType === 'total' // •`_´•
  );

  const hasMore = tableData.length > tableDataSlice.length;

  const withShowMoreButton = proOnlydisplayLimit && hasMore && hasCoreProPlus;
  const showBuyCoreButton = proOnlydisplayLimit && hasMore && !hasCoreProPlus;

  const selectItems = availableTimePeriods.map(option => ({
    value: option.value,
    label: option.translationKey
      ? t(option.translationKey)
      : option.label || t(`studio.reports.timePeriods.${option.value}`),
    biData: BiData<StatsChangeLevelClickBiEvent>({
      evid: 831,
      sectionname: reportType,
      time_period: option.value,
    }),
  }));

  return (
    <Surface
      type={ThemeSurface.SECONDARY}
      themeOverrideOnly
      className={c(
        s['root'],
        (showTotal || withShowMoreButton || showBuyCoreButton) &&
          s['no-bottom-padding']
      )}
      tag={Section}
      key={timePeriod}
    >
      <Heading className={s['heading']}>{t(headingKey)}</Heading>
      <div className={s['subheading']}>
        {t(descriptionKey, descriptionTranslationOptions)}
      </div>

      <SelectField
        id="time-frame-select"
        name="time-frame-select"
        label="time frame"
        isLabelHidden
        className={s['time-frame-selector']}
        value={timePeriod}
        onSelect={(val: TimePeriod) => setTimePeriod(val)}
        items={selectItems}
        withBlankOption={false}
      />

      {tableDataSlice.length > 0 ? (
        <>
          {tableDataSlice.map((item, idx) => (
            <ReportsTableRowMobile
              key={generateReportsTableItemKey(item, idx)}
              compact={compact}
              extras={RowExtras && <RowExtras item={item} />}
            >
              <RowHeading item={item} index={idx} />
              <ReportsDataRowMobile
                data={columns.map(col => ({
                  label: col.label,
                  value: String(
                    col.format
                      ? col.format(item[col.key], item)
                      : (item[col.key] ?? '')
                  ),
                  className: col.className,
                }))}
              />
            </ReportsTableRowMobile>
          ))}

          {withShowMoreButton && (
            <TextButton
              className={s['show-more-button']}
              size="small"
              variant="main"
              onClick={() => setIsShowingAllRows(true)}
              postfix={
                <ArrowDown size={IconSize.MEDIUM} disableDefaultColors />
              }
            >
              {t('studio.reports.section.common.table.showMore')}
            </TextButton>
          )}

          {showBuyCoreButton && (
            <div className={s['core-upsell']}>
              <div className={s['text']}>
                {t('studio.reports.section.topSellers.table.coreUpsell.text')}
              </div>
              <BrandedButton
                size="medium"
                variant="core-orange"
                href={Url.coreMembershipLink()}
                target="_blank"
                biData={BiData<BuyCoreButtonBiEvent>({
                  evid: 375,
                  referral_info: ReferralType.STUDIO_ANALYTICS,
                })}
              >
                {t('studio.reports.section.topSellers.table.coreUpsell.cta')}
              </BrandedButton>
            </div>
          )}

          {showTotal && totalData && (
            <ReportsDataTotalRowMobile
              data={columns.map(col => ({
                label: col.label,
                value: String(
                  col.format
                    ? col.format(totalData[col.key], col)
                    : (totalData[col.key] ?? '')
                ),
                className: col.className,
              }))}
            />
          )}
        </>
      ) : (
        <SectionEmptyState
          title={t(`studio.reports.section.empty.${timePeriod}.title`)}
          body={t(`studio.reports.section.empty.body`)}
        />
      )}
    </Surface>
  );
};

GenericReportMobile.displayName = 'GenericReportMobile';
export default GenericReportMobile;
